import { mutation } from '@/dex-shared/helpers/graphql';
import ContentModel from '@/dex-shared/models/ContentModel';

export default {
  async CREATE_AND_ASSOCIATE_CONTENT(
    { dispatch }: any,
    { aggregatorId, type }: any,
  ) {
    let { questionId, solutionId, articleId, assertionId } = aggregatorId;
    if (Object.values(ContentModel.__types).includes(type)) {
      let id = await dispatch(`CREATE_${type.toUpperCase()}`);
      dispatch('CREATE_CONTENT', {
        questionId,
        solutionId,
        articleId,
        assertionId,
        [`${type.toLowerCase()}Id`]: id,
      });
    } else {
      throw new Error(`Content type ${type} was not recognized`);
    }
  },
  async UPDATE_CONTENTS(_: any, contents: Array<ContentModel>) {
    const orderedContents = contents.sort((A, B) => A.position - B.position);

    await resetAllContentsPositions(orderedContents);

    return Promise.all(
      orderedContents.map(async (content: ContentModel, index) => {
        await updateContentPosition(content.id, index);

        if (content.textId) {
          return mutation(
            `mutation UpdateText ($id: Int!, $body: String!,
                $source: JSON, $alignment: Alignmentenum) {
              updateTextById(
                input: {
                  id: $id
                  textPatch: {
                    body: $body
                    source: $source
                    alignment: $alignment
                  }
                }
              ) {
                text {
                  id
                }
              }
            }`,
            content.text().raw(),
          );
        } else if (content.imageId) {
          return mutation(
            `mutation UpdateImage ($id: Int!, $url: String!, $source: JSON,
                $resizeFactor: Float, $description: String) {
              updateImageById(
                input: {
                  id: $id
                  imagePatch: {
                    url: $url
                    source: $source
                    resizeFactor: $resizeFactor
                    description: $description
                  }
                }
              ) {
                image {
                  id
                }
              }
            }`,
            content.image().raw(),
          );
        } else if (content.videoId) {
          return mutation(
            `mutation UpdateVideo ($id: Int!, $providerType: Videoproviderenum,
                $providerId: String, $source: JSON) {
              updateVideoById(
                input: {
                  id: $id
                  videoPatch: {
                    providerType: $providerType
                    providerId: $providerId
                    source: $source
                  }
                }
              ) {
                video {
                  id
                }
              }
            }`,
            content.video().raw(),
          );
        }
      }),
    );
  },

  async CREATE_TEXT({ commit, rootState }) {
    let userId = rootState.auth.user.id;
    let response = await mutation(
      `
    mutation CreateText($userId: Int!) {
      createText(input: {
        text: {
          userId: $userId
          body: ""
        }
      }) {
        text {
          id
          body
        }
      }
    }`,
      {
        userId,
      },
    );
    commit('UPDATE_ENTITY', {
      entity: 'texts',
      data: response.data.createText.text,
    });
    return response.data.createText.text.id;
  },

  async CREATE_IMAGE({ commit, rootState }) {
    let userId = rootState.auth.user.id;
    let response = await mutation(
      `
    mutation CreateImage($userId: Int!) {
      createImage(input: {
        image: {
          userId: $userId
          url: ""
          resizeFactor: 100
        }
      }) {
        image {
          id
        }
      }
    }`,
      {
        userId,
      },
    );
    commit('UPDATE_ENTITY', {
      entity: 'images',
      data: response.data.createImage.image,
    });
    return response.data.createImage.image.id;
  },

  async CREATE_VIDEO({ commit, rootState }) {
    let userId = rootState.auth.user.id;
    let response = await mutation(
      `
    mutation CreateVideo($userId: Int!, $providerType: Videoproviderenum!) {
      createVideo(input: {
        video: {
          userId: $userId
          providerType: $providerType
          providerId: ""
        }
      }) {
        video {
          id
          providerType
          providerId
        }
      }
    }`,
      {
        userId,
        providerType: 'YOUTUBE',
      },
    );
    commit('UPDATE_ENTITY', {
      entity: 'videos',
      data: response.data.createVideo.video,
    });
    return response.data.createVideo.video.id;
  },

  async CREATE_CONTENT({ commit, rootState }, content: any) {
    let userId = +rootState.auth.user.id;
    let response = await mutation(
      `mutation AssignContent($userId: Int, $position: Int,
         $imageId: Int, $textId: Int, $videoId: Int, $assertionId: Int
         $questionId: Int, $solutionId: Int, $articleId: Int) {
      createContent(input: {
        content: {
          userId: $userId
          imageId: $imageId
          textId: $textId
          videoId: $videoId
          articleId: $articleId
          questionId: $questionId
          solutionId: $solutionId
          assertionId: $assertionId
          position: $position
        }
      }) {
        content {
          id
          imageId
          textId
          videoId
          articleId
          questionId
          solutionId
          assertionId
          position
        }
      }
    }`,
      {
        ...content,
        userId,
      },
    );
    commit('UPDATE_ENTITY', {
      entity: 'contents',
      data: response.data.createContent.content,
    });
  },

  async DELETE_CONTENT({ commit, dispatch }, contentId: number) {
    let response = await mutation(
      `mutation DeleteContent($contentId: Int!) {
        deleteContentById(input: {
          id: $contentId
        }) {
          content {
            id
            imageId
            textId
            videoId
          }
        }
      }`,
      {
        contentId,
      },
    );
    if (!response.errors) {
      commit('DELETE_ENTITY', {
        entity: 'contents',
        id: contentId,
      });
      let {
        imageId,
        textId,
        videoId,
      } = response.data.deleteContentById.content;
      if (imageId) {
        dispatch('DELETE_IMAGE', imageId);
      } else if (textId) {
        dispatch('DELETE_TEXT', textId);
      } else if (videoId) {
        dispatch('DELETE_VIDEO', videoId);
      }
    }
  },

  async DELETE_IMAGE({ commit }, imageId: number) {
    let response = await mutation(
      `mutation dexAdminDeleteImage($imageId: Int!) {
        deleteImageById(input: {
          id: $imageId
        }) {
          image {
            id
          }
        }
      }`,
      {
        imageId,
      },
    );
    if (!response.errors) {
      commit('DELETE_ENTITY', {
        entity: 'images',
        id: imageId,
      });
    }
  },

  async DELETE_TEXT({ commit }, textId: number) {
    let response = await mutation(
      `mutation dexAdminDeleteTextById($textId: Int!) {
        deleteTextById(input: {
          id: $textId
        }) {
          text {
            id
          }
        }
      }`,
      {
        textId,
      },
    );
    if (!response.errors) {
      commit('DELETE_ENTITY', {
        entity: 'texts',
        id: textId,
      });
    }
  },

  async DELETE_VIDEO({ commit }, videoId: number) {
    let response = await mutation(
      `mutation dexAdminDeleteVideoById($videoId: Int!) {
        deleteVideoById(input: {
          id: $videoId
        }) {
          video {
            id
          }
        }
      }`,
      {
        videoId,
      },
    );
    if (!response.errors) {
      commit('DELETE_ENTITY', {
        entity: 'videos',
        id: videoId,
      });
    }
  },
};

const resetAllContentsPositions = async (contents: Array<ContentModel>) => {
  for (const content of contents) await updateContentPosition(content.id, null);
};

const updateContentPosition = async (contentId: number, position: number) => {
  const updateContentPositionQuery = `mutation UpdateContentPosition($id: Int!, $position: Int){
    updateContentById(
      input: {
        id: $id
        contentPatch: {
          position: $position
        }
      }
    ) {
      content {
        id
      }
    }
  }`;
  return mutation(updateContentPositionQuery, {
    id: contentId,
    position,
  });
};
